import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface HealthStatus {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  count?: number;
  error?: string;
  details?: string;
}

const DataHealthCheck: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const checkDataHealth = async () => {
    setIsLoading(true);
    const checks: HealthStatus[] = [];

    try {
      // Check course categories
      const { data: categories, error: catError } = await supabase
        .from('course_categories')
        .select('*');
      
      checks.push({
        name: 'Course Categories',
        status: catError ? 'error' : categories?.length ? 'healthy' : 'warning',
        count: categories?.length || 0,
        error: catError?.message,
        details: `${categories?.length || 0} categories found`
      });

      // Check courses table
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .eq('is_active', true);
      
      checks.push({
        name: 'Courses Table',
        status: coursesError ? 'error' : courses?.length ? 'healthy' : 'warning',
        count: courses?.length || 0,
        error: coursesError?.message,
        details: `${courses?.length || 0} active courses found`
      });

      // Check course overview
      const { data: overview, error: overviewError } = await supabase
        .from('course_overview')
        .select('*')
        .eq('is_active', true);
      
      checks.push({
        name: 'Course Overview',
        status: overviewError ? 'error' : overview?.length ? 'healthy' : 'warning',
        count: overview?.length || 0,
        error: overviewError?.message,
        details: `${overview?.length || 0} courses in overview`
      });

      // Check profiles access
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
      
      checks.push({
        name: 'Profiles Access',
        status: profileError ? 'error' : 'healthy',
        error: profileError?.message,
        details: profileError ? 'Cannot access profiles' : 'Profiles accessible'
      });

      // Check user roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .limit(5);
      
      checks.push({
        name: 'User Roles',
        status: rolesError ? 'error' : 'healthy',
        count: roles?.length || 0,
        error: rolesError?.message,
        details: `${roles?.length || 0} user roles found`
      });

    } catch (error: any) {
      checks.push({
        name: 'General Data Access',
        status: 'error',
        error: error.message,
        details: 'Failed to perform health checks'
      });
    }

    setHealthStatus(checks);
    setIsLoading(false);
  };

  useEffect(() => {
    checkDataHealth();
  }, []);

  const getStatusIcon = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusColor = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Data Health Check</CardTitle>
          <Button
            onClick={checkDataHealth}
            disabled={isLoading}
            variant="outline"
            size="sm"
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {healthStatus.map((check, index) => (
            <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(check.status)}
                <div>
                  <div className="font-medium">{check.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {check.details}
                  </div>
                  {check.error && (
                    <div className="text-sm text-red-600 mt-1">
                      Error: {check.error}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {check.count !== undefined && (
                  <span className="text-sm font-mono">{check.count}</span>
                )}
                <Badge className={getStatusColor(check.status)}>
                  {check.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataHealthCheck;